import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  GeckoPolygonalPlate,
  GeckoPolygonalPlateJSON,
  parseCoordinatesFromString,
} from 'types/assetModel/features/geckoPolygonalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import {
  requiredCoordinatesArraySchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoShipDeckDrainJSON {
  type: GeckoFeatureModelType.ShipDeckDrain
  name: string
  feature_geometry: {
    coordinates: { x: number; y: number }[]
  }
}

export class GeckoShipDeckDrain extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckDrain
  readonly polygonalPlate: GeckoPolygonalPlate

  /**
   * Private constructor, allowing for a factory method that can return undefined
   * @param id
   * @param name
   * @param polygonalPlate
   */

  private constructor(
    id: string,
    name: string,
    polygonalPlate: GeckoPolygonalPlate
  ) {
    super(id)
    this.name = name
    this.polygonalPlate = polygonalPlate
    if (this.polygonalPlate.geometry.length < 4) {
      throw new Error('Drain must have at least 4 points in its geometry')
    }
  }

  public static CreateFromJSON(
    componentID: string,
    drainID: string,
    drainJSON: GeckoShipDeckDrainJSON
  ): GeckoShipDeckDrain {
    const drainName = drainJSON.name as string

    const polygonalPlateJSON: GeckoPolygonalPlateJSON = {
      type: GeckoFeatureModelType.PolygonalPlate,
      name: `${drainName} Plate`,
      feature_geometry: drainJSON.feature_geometry,
      installation_date: undefined,
    }

    const polygonalPlate = GeckoPolygonalPlate.CreateFromJSON(
      componentID,
      `${drainID}_plate`,
      polygonalPlateJSON
    )

    return new GeckoShipDeckDrain(drainID, drainName, polygonalPlate)
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckDrainJSON>
  ): Map<string, GeckoShipDeckDrain> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckDrain.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckDrain>())
  }

  asJSON() {
    return generateDrain(this.name, this.polygonalPlate)
  }
}

export function generateDrain(
  name: string,
  polygonalPlate: GeckoPolygonalPlate
): GeckoShipDeckDrainJSON {
  const plateJSON = polygonalPlate.asJSON()
  return {
    type: GeckoFeatureModelType.ShipDeckDrain,
    name: name,
    feature_geometry: plateJSON.feature_geometry,
  }
}

export const NewGeckoShipDeckDrainJSON: GeckoShipDeckDrainJSON = {
  type: GeckoFeatureModelType.ShipDeckDrain,
  name: 'New Ship Deck Drain',
  feature_geometry: {
    coordinates: [
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ],
  },
}

export const ShipDeckDrainFormFields = (drainJSON: GeckoShipDeckDrainJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Drain Name',
      value: drainJSON.name,
      unit_magnitude: null,
      initialValue: 'Drain',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coordinates',
      label: 'Coordinates',
      value: drainJSON.feature_geometry.coordinates
        .map((coordinate) => `[${coordinate.x}, ${coordinate.y}]`)
        .join(', '),
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredCoordinatesArraySchema(4),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Coordinates,
    },
  ]
}

export const ShipDeckDrainJSONFromFormFields = (formFields: FormField[]) => {
  const coordinates = getFormFieldByDataKey(formFields, 'coordinates')
    ?.value as string

  const polygonalPlateJSON: GeckoPolygonalPlateJSON = {
    type: GeckoFeatureModelType.PolygonalPlate,
    name: `${getFormFieldByDataKey(formFields, 'name')?.value as string} Plate`,
    feature_geometry: { coordinates: parseCoordinatesFromString(coordinates) },
    installation_date: undefined,
  }

  const polygonalPlate = GeckoPolygonalPlate.CreateFromJSON(
    polygonalPlateJSON.name,
    polygonalPlateJSON.type,
    polygonalPlateJSON
  )

  return generateDrain(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    polygonalPlate
  )
}

export const ShipDeckDrainFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckDrain,
  title: 'Drains',
  singularName: 'Ship Deck Drain',
  fields: ['name', 'coordinates'],
  tableMessage:
    'To see how to enter coordinates to render a drain correctly, please see the <a href="https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/4595744769/Polygonal+Plates" target="_blank">documentation</a> on polygonal plates.',
}
